/**
 * KindAwareInventoryPanel — kind-specific replacement for the
 * EventDashboard "Tickets" tab.
 *
 * - event       → existing TicketTypesPanel (passed-through via prop)
 * - experience  → wraps ExperienceSlotManager with an explainer header so
 *                 organizers know slots ARE the inventory for experiences
 * - reservation → live bookings table fed by GET /api/reservations/{event_id}
 *                 + a recap of the deposit / party / threshold rules. No
 *                 ticket tiers — the venue page books directly into the
 *                 reservations collection.
 */
import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Loader2, Users, Calendar, Clock, RefreshCw, MapPin, Utensils, Sparkles, Settings } from 'lucide-react';
import ExperienceSlotManager from './ExperienceSlotManager';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtDate = (iso) => {
  if (!iso) return '';
  try {
    return new Date(`${iso}T00:00:00`).toLocaleDateString('en-US', {
      weekday: 'short', month: 'short', day: 'numeric', year: 'numeric',
    });
  } catch { return iso; }
};

const fmtTime = (t) => {
  if (!t) return '';
  const [h, m] = String(t).split(':').map(Number);
  if (Number.isNaN(h)) return t;
  const p = h >= 12 ? 'PM' : 'AM';
  const h12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
  return `${h12}:${String(m || 0).padStart(2, '0')} ${p}`;
};

const fmtMoney = (cents) => typeof cents === 'number' ? `$${(cents / 100).toFixed(2)}` : '$0.00';

// ─────────── Experience ───────────────────────────────────────────────
function ExperienceInventory({ event, eventId, onEditSettings }) {
  const token = localStorage.getItem('eventism_token') || '';
  return (
    <div className="space-y-4">
      <Card>
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <Sparkles className="h-5 w-5 text-fuchsia-400 mt-0.5 flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-bold mb-1">Experience slots</p>
              <p className="text-xs text-muted-foreground leading-relaxed">
                Each slot is one bookable session (date · time · price · capacity).
                Guests pick a slot and pay the per-guest price. Add recurring
                tastings, weekend tours, daily classes — Eventism handles
                the inventory.
              </p>
              {event?.duration_minutes ? (
                <p className="text-[11px] text-muted-foreground mt-2">
                  Session length · <span className="text-foreground font-semibold">{event.duration_minutes} min</span>
                  {event.max_group_size ? <> · Max group · <span className="text-foreground font-semibold">{event.max_group_size}</span></> : null}
                </p>
              ) : null}
            </div>
            {onEditSettings && (
              <Button
                size="sm" variant="outline"
                onClick={onEditSettings}
                data-testid="experience-edit-settings"
                className="flex-shrink-0"
              >
                <Settings className="h-3.5 w-3.5 mr-1.5" /> Edit settings
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
      <ExperienceSlotManager eventId={eventId} token={token} />
    </div>
  );
}

// ─────────── Reservation ──────────────────────────────────────────────
function ReservationInventory({ event, eventId, onEditSettings }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/reservations/${eventId}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('eventism_token') || ''}` },
      });
      setRows(res.data?.reservations || res.data || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load reservations');
    } finally {
      setLoading(false);
    }
  }, [eventId]);

  useEffect(() => { load(); }, [load]);

  const partyMin = Number(event?.party_size_min) || 1;
  const partyMax = Number(event?.party_size_max) || 8;
  const depositCents = Number(event?.deposit_amount_cents) || 0;
  const threshold = Number(event?.large_party_threshold) || 0;

  return (
    <div className="space-y-4">
      {/* Reservation rules card */}
      <Card>
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <Utensils className="h-5 w-5 text-emerald-400 mt-0.5 flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-bold mb-1">Reservation rules</p>
              <p className="text-xs text-muted-foreground leading-relaxed">
                Parties of <span className="text-foreground font-semibold">{partyMin}–{partyMax}</span> guests.{' '}
                {depositCents > 0 ? (
                  <>
                    Deposit of <span className="text-foreground font-semibold">{fmtMoney(depositCents)}/guest</span>
                    {threshold ? <> kicks in for parties of <span className="text-foreground font-semibold">{threshold}+</span></> : ' on every booking'}.
                  </>
                ) : (
                  'No deposit — every booking is a free hold.'
                )}
              </p>
              <p className="text-[11px] text-muted-foreground mt-2">
                Need to change party size, deposit, seating areas or dress code? Use Edit settings.
              </p>
            </div>
            {onEditSettings && (
              <Button
                size="sm" variant="outline"
                onClick={onEditSettings}
                data-testid="reservation-edit-settings"
                className="flex-shrink-0"
              >
                <Settings className="h-3.5 w-3.5 mr-1.5" /> Edit settings
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Bookings list */}
      <Card>
        <CardContent className="p-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-bold flex items-center gap-2">
              <Calendar className="h-4 w-4 text-emerald-300" />
              Bookings <Badge variant="secondary" className="ml-1">{rows.length}</Badge>
            </h3>
            <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={load} disabled={loading} data-testid="reservation-refresh">
              {loading ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <RefreshCw className="h-3 w-3 mr-1" />}
              Refresh
            </Button>
          </div>
          {loading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="h-5 w-5 animate-spin text-emerald-400" />
            </div>
          ) : rows.length === 0 ? (
            <p className="text-xs text-muted-foreground text-center py-8">
              No bookings yet. They&apos;ll show up here the moment a guest reserves a table.
            </p>
          ) : (
            <div className="space-y-2" data-testid="reservation-rows">
              {rows.map((r) => (
                <div
                  key={r.reservation_id || `${r.date}-${r.start_time}-${r.email}`}
                  className="flex items-start justify-between gap-3 rounded-lg border border-border bg-muted/30 p-3"
                  data-testid={`reservation-row-${r.reservation_id || ''}`}
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="font-semibold text-sm truncate">{r.name || r.email}</p>
                      <Badge variant="outline" className="text-[10px]">
                        {r.party_size} {r.party_size === 1 ? 'guest' : 'guests'}
                      </Badge>
                      {r.payment_status === 'paid' && (
                        <Badge className="bg-emerald-500 text-black text-[10px]">Prepaid</Badge>
                      )}
                      {r.status === 'cancelled' && (
                        <Badge variant="outline" className="text-red-400 border-red-400/40 text-[10px]">Cancelled</Badge>
                      )}
                    </div>
                    <div className="text-xs text-muted-foreground mt-1 flex items-center gap-3 flex-wrap">
                      <span className="flex items-center gap-1"><Calendar className="h-3 w-3" />{fmtDate(r.date)}</span>
                      <span className="flex items-center gap-1"><Clock className="h-3 w-3" />{fmtTime(r.start_time)}</span>
                      {r.seating_area && <span className="flex items-center gap-1"><MapPin className="h-3 w-3" />{r.seating_area}</span>}
                    </div>
                    {r.special_requests && (
                      <p className="text-[11px] text-muted-foreground mt-1 italic line-clamp-2">
                        &quot;{r.special_requests}&quot;
                      </p>
                    )}
                  </div>
                  <div className="text-right flex-shrink-0">
                    <p className="text-xs text-muted-foreground">{r.email}</p>
                    {r.phone && <p className="text-xs text-muted-foreground">{r.phone}</p>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// ─────────── Exported wrapper ─────────────────────────────────────────
export default function KindAwareInventoryPanel({ event, eventId, ticketsPanel, onEditSettings }) {
  const kind = event?.kind || 'event';
  if (kind === 'experience')  return <ExperienceInventory  event={event} eventId={eventId} onEditSettings={onEditSettings} />;
  if (kind === 'reservation') return <ReservationInventory event={event} eventId={eventId} onEditSettings={onEditSettings} />;
  return ticketsPanel;
}
